import type { CSSProperties } from 'react';
import { Check } from '../Icons';
import { Brand } from '../Brand';

// Scattered "nodes" in the chaos network. top/left are percentages of the stage;
// the connecting SVG lines use the same coordinates so they meet at each node.
const CHAOS = [
  { label: 'Flexible working request', top: 15, left: 31, rot: -5, delay: '0s', color: '#A15862' },
  { label: 'Right to work check', top: 13, left: 68, rot: 3, delay: '2.1s', color: '#5862A1' },
  { label: 'Sickness absence', top: 32, left: 72, rot: 4, delay: '1.4s', color: '#62A158' },
  { label: 'Grievance', top: 45, left: 60, rot: 6, delay: '0.7s', color: '#A15862' },
  { label: 'Disability adjustment', top: 50, left: 22, rot: -3, delay: '2.7s', color: '#5862A1' },
  { label: 'Maternity leave', top: 67, left: 73, rot: -6, delay: '1.1s', color: '#62A158' },
  { label: 'Sexual harassment prevention policy', top: 71, left: 43, rot: -4, delay: '3.4s', color: '#B06B73' },
  { label: 'Disciplinary process', top: 83, left: 27, rot: 5, delay: '3.9s', color: '#A15862' },
];

const NET_LINES =
  // the inner web between the topic nodes
  'M31,15 L68,13 M31,15 L22,50 M68,13 L72,32 M72,32 L60,45 M60,45 L22,50 ' +
  'M22,50 L43,71 M43,71 L73,67 M60,45 L73,67 M43,71 L27,83 M22,50 L27,83 ' +
  // branches that reach out past the frame so the network feels boundless
  'M31,15 L9,-9 M68,13 L82,-8 M72,32 L103,25 M73,67 L103,80 ' +
  'M27,83 L12,111 M43,71 L50,113 M22,50 L-15,55 M60,45 L102,50 M27,83 L-13,92';

const CALM = [
  { label: 'Flexible working request', status: 'Handled' },
  { label: 'Disability adjustment', status: 'Handled' },
  { label: 'Sickness absence', status: 'Logged' },
  { label: 'Sexual harassment prevention policy', status: 'Policy ready' },
  { label: 'Grievance', status: 'Resolved' },
  { label: 'Right to work check', status: 'Handled' },
];

export default function Contrast() {
  return (
    <section className="section container contrast">
      <div className="contrast__head">
        <span className="eyebrow">From hours to minutes</span>
		<h2 className="contrast__title">All the things you're juggling, taken care of.</h2>
      </div>

      <div className="contrast__grid">
        {/* Chaos */}
        <div className="contrast__col">
          <span className="contrast__label contrast__label--chaos">
            Without <Brand />
          </span>
          <div className="chaos">
            <svg
              className="chaos__net"
              viewBox="-18 -12 122 132"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path
                d={NET_LINES}
                fill="none"
                stroke="rgba(38,34,28,0.13)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            {CHAOS.map((c) => (
              <span
                key={`node-${c.label}`}
                className="chaos__node"
                style={{ top: `${c.top}%`, left: `${c.left}%` }}
              />
            ))}
            {CHAOS.map((c) => (
              <div
                key={c.label}
                className="chaos__card"
                style={
                  {
                    top: `${c.top}%`,
                    left: `${c.left}%`,
                    animationDelay: c.delay,
                    '--rot': `${c.rot}deg`,
                  } as CSSProperties
                }
              >
                <span className="chaos__dot" style={{ background: c.color }} />
                {c.label}
              </div>
            ))}
          </div>
        </div>

        {/* Calm */}
        <div className="contrast__col">
          <span className="contrast__label contrast__label--calm">
            With <Brand />
          </span>
          <div className="calm">
            <div className="calm__head">
              <span className="calm__title">Your HR</span>
              <span className="calm__badge">All caught up</span>
            </div>
            <div className="calm__rows">
              {CALM.map((c) => (
                <div className="calm__row" key={c.label}>
                  <span className="calm__check">
                    <Check size={11} color="#fff" strokeWidth={3.4} />
                  </span>
                  <span className="calm__label">{c.label}</span>
                  <span className="calm__status">{c.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
